import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import Layout from '../../components/Layout';
import { Edit, Trash2, Eye } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import SlideOver from '../../components/ui/SlideOver';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { ClientForm, getClientDisplayName } from './clientsForms';

const ALPHABET = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
/** Sentinel for API `name_prefix`: names starting with a digit or non-letter */
const NAME_PREFIX_NON_ALPHA = '123..';

const Clients = () => {
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [namePrefix, setNamePrefix] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [pagination.page, search, clientTypeFilter, activeFilter, namePrefix]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/clients', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search,
          client_type: clientTypeFilter,
          is_active: activeFilter,
          ...(namePrefix ? { name_prefix: namePrefix } : {})
        }
      });
      
      setClients(response.data.clients);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load clients');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!confirm('Are you sure you want to deactivate this client?')) return;

    try {
      await api.delete(`/clients/${clientId}`);
      fetchClients(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete client');
      console.error('Delete error:', err);
    }
  };

  const isPristineEmptyView =
    !loading &&
    clients.length === 0 &&
    !search &&
    !clientTypeFilter &&
    !namePrefix &&
    activeFilter === 'true';

  return (
    <TooltipProvider>
      <Layout>
        {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Clients
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your customer database
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="secondary"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
              >
                + New Client
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormLabel>Search</FormLabel>
              <Input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Search by name or email..."
                className="h-8"
              />
            </div>
            <div>
              <FormLabel>Type</FormLabel>
              <Select
                value={clientTypeFilter}
                onChange={(e) => {
                  setClientTypeFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Types</option>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </Select>
            </div>
            <div>
              <FormLabel>Status</FormLabel>
              <Select
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </div>
          </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
            {error}
          </div>
        )}

        {/* Clients Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading clients...</p>
            </div>
          ) : isPristineEmptyView ? (
            <div className="p-8 text-center text-[var(--ds-text-muted)]">
              <p className="text-xs">No clients found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-xs text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] font-medium"
              >
                Create your first client
              </button>
            </div>
          ) : (
            <>
              <div
                className="border-b border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] px-3 py-2 sm:px-4"
                role="navigation"
                aria-label="Jump to clients by first letter or number"
              >
                <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
                  <span className="text-[11px] font-medium text-[var(--ds-text-muted)] mr-1 shrink-0">
                    Jump:
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setNamePrefix('');
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className={`min-w-[1.75rem] rounded-[var(--ds-radius-sm)] px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                      !namePrefix
                        ? 'bg-[var(--ds-accent-primary)]/15 text-[var(--ds-accent-primary)]'
                        : 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-surface-elevated)] hover:text-[var(--ds-text-primary)]'
                    }`}
                  >
                    All
                  </button>
                  {ALPHABET.map((letter) => (
                    <button
                      key={letter}
                      type="button"
                      onClick={() => {
                        setNamePrefix(letter);
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      aria-label={`Show clients starting with ${letter}`}
                      className={`min-w-[1.5rem] rounded-[var(--ds-radius-sm)] px-1 py-0.5 text-[11px] font-semibold transition-colors ${
                        namePrefix === letter
                          ? 'bg-[var(--ds-accent-primary)]/15 text-[var(--ds-accent-primary)]'
                          : 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-surface-elevated)] hover:text-[var(--ds-text-primary)]'
                      }`}
                    >
                      {letter}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setNamePrefix(NAME_PREFIX_NON_ALPHA);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    aria-label="Show clients whose name starts with a number or symbol"
                    className={`min-w-[2.25rem] rounded-[var(--ds-radius-sm)] px-1.5 py-0.5 text-[11px] font-semibold transition-colors ${
                      namePrefix === NAME_PREFIX_NON_ALPHA
                        ? 'bg-[var(--ds-accent-primary)]/15 text-[var(--ds-accent-primary)]'
                        : 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-surface-elevated)] hover:text-[var(--ds-text-primary)]'
                    }`}
                  >
                    123..
                  </button>
                </div>
              </div>
              {clients.length === 0 ? (
                <div className="p-8 text-center text-[var(--ds-text-muted)]">
                  <p className="text-xs">No clients match your current filters.</p>
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell
                        className="whitespace-nowrap cursor-pointer"
                        onClick={() => navigate(`/tenant/clients/${client.id}`)}
                      >
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-[rgba(47,123,255,0.20)] flex items-center justify-center text-[var(--ds-accent-primary)] font-bold text-[12px]">
                                {getClientDisplayName(client).charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-[12px] font-medium text-[var(--ds-text-primary)]">
                                {getClientDisplayName(client)}
                              </div>
                              {client.credit_limit && (
                                <div className="text-[12px] text-[var(--ds-text-muted)]">
                                  Credit: £{parseFloat(client.credit_limit).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {client.email && (
                          <div className="text-[12px] text-[var(--ds-text-primary)]">{client.email}</div>
                        )}
                        {client.phone && (
                          <div className="text-[12px] text-[var(--ds-text-muted)]">{client.phone}</div>
                        )}
                        {client.mobile && (
                          <div className="text-[12px] text-[var(--ds-text-muted)]">Mobile: {client.mobile}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="text-[12px] text-[var(--ds-text-primary)]">
                          {client.city || '-'}
                        </div>
                        {client.postcode && (
                          <div className="text-[12px] text-[var(--ds-text-muted)]">{client.postcode}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className={`text-[12px] font-medium ${
                          parseFloat(client.current_balance || 0) >= 0 
                            ? 'text-[var(--ds-text-primary)]' 
                            : 'text-[var(--ds-status-danger)]'
                        }`}>
                          £{parseFloat(client.current_balance || 0).toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant={client.is_active ? 'success' : 'neutral'} className="text-[12px]">
                          {client.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell 
                        className="whitespace-nowrap text-right text-[12px] font-medium space-x-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                          <Tooltip content="View">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/clients/${client.id}`)}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => {
                                setSelectedClient(client);
                                setShowEditModal(true);
                              }}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Deactivate">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleDeleteClient(client.id)}
                              disabled={!client.is_active}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-[var(--ds-surface-panel)] px-4 py-3 border-t border-[var(--ds-border-divider)] sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-[var(--ds-text-secondary)]">
                      Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                      {' '}({pagination.total} total clients)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Create Client Slide Over */}
      <SlideOver
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
          }
        }}
        title="New Client"
      >
        {showCreateModal && (
          <ClientForm
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchClients();
            }}
          />
        )}
      </SlideOver>
      
      {/* Edit Client Slide Over */}
      <SlideOver
        open={showEditModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditModal(false);
            setSelectedClient(null);
          }
        }}
        title={selectedClient ? `Edit ${getClientDisplayName(selectedClient)}` : 'Edit Client'}
      >
        {showEditModal && selectedClient && (
          <ClientForm
            client={selectedClient}
            onClose={() => {
              setShowEditModal(false);
              setSelectedClient(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedClient(null);
              fetchClients();
            }}
          />
        )}
      </SlideOver>
      </Layout>
    </TooltipProvider>
  );
};

export default Clients;
